import { apiServerUrl, ExternalApi } from "@/apis/apiService";
import {
    useCheckoutStore,
    useSessionStore,
    useIFXStore,
    usePaymentsStore,
} from "@/plugins/store";
import { keysToCamelCase, keysToSnakeCase } from "@/helpers/stringProcessors";
import {
    CreateOrderResponse,
    OrderAdditionalInformation,
} from "@/helpers/interfaces/shared";
import { getRequestConfig } from "@esenda/core-ui/utils";
import { getCommonHeaderParams } from "@/helpers/api";
import { HTTP_METHOD } from "@esenda/core-ui/constants";
import { useOrganisationStore } from "@/plugins/store";

export const createOrder = async (alertAction = "retry") => {
    /**
     * Internal API call to create a order record
     * @param  {[string | null]} alertAction - display action alert or not, default to refresh
     * @return {[{CreateOrderResponse, null}]}
     */
    const sessionsStore = useSessionStore();
    const orgStore = useOrganisationStore();
    const checkoutStore = useCheckoutStore();

    checkoutStore.supplementaryData.paymentOriginCountry =
        sessionsStore.countryCode;

    const orderPayload = keysToSnakeCase({
        shortcode: orgStore.shortcode,
        emailAddress: sessionsStore.emailAddress,
        formName: orgStore.formName,
        formData: keysToSnakeCase(checkoutStore.supplementaryData),
    });

    /** Define the behaviour if an error occurs during the API call */
    const errorHandlerConfig = {
        userAction: alertAction,
        sentryTransactionName: "createOrder",
    };

    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/orders`,
            HTTP_METHOD.POST,
            getCommonHeaderParams(),
            orderPayload,
        ),
        errorHandlerConfig,
    );

    if (data !== null) {
        const orderInformation: CreateOrderResponse = keysToCamelCase(data);
        checkoutStore.setOrderId(orderInformation.identifier);
        return orderInformation;
    } else {
        return null;
    }
};

export const getOrderAdditionalInformation = async (identifier: string) => {
    /**
     * Internal API call to retrieve additional order information. If
     * successful, it will save the information in the session storage
     * @param  {[string]} identifier - transaction uuid
     * @return {[{void}]}
     */
    const sessionStore = useSessionStore();
    const checkoutStore = useCheckoutStore();
    /** Define the behaviour if an error occurs during the API call */
    const errorHandlerConfig = {
        userAction: "refresh", // user needs to refresh the browser
        sentryTransactionName: "getOrderAdditionalInformation",
    };

    const externalApi = new ExternalApi();
    const { data, error } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/orders/${identifier}/additional-information`,
            HTTP_METHOD.GET,
            getCommonHeaderParams(),
        ),
        errorHandlerConfig,
    );

    if (data !== null) {
        const additionalInformation: OrderAdditionalInformation =
            keysToCamelCase(data);

        checkoutStore.setPayerDetails({
            firstName: additionalInformation["payorFirstName"],
            lastName: additionalInformation["payorLastName"],
            address: {
                addressLine1: "",
                addressLine2: "",
                addressLine3: "",
                city: "",
                state: "",
                postalCode: "",
                countryCode: sessionStore.countryCode,
            },
        });
        sessionStore.setEmailAddress(
            additionalInformation.emailAddress ?? null,
        );
        /** Update the supplementary data if it's returned in the endpoint's response */
        if ("supplementaryData" in additionalInformation) {
            checkoutStore.updateSupplementaryData(
                additionalInformation.supplementaryData,
            );
        }

        if ("returnUrl" in additionalInformation) {
            checkoutStore.updatePartnerLinkInformation({
                returnUrl: String(additionalInformation["returnUrl"]),
            });
        }

        if ("linkExpiryDate" in additionalInformation) {
            checkoutStore.updatePartnerLinkInformation({
                linkExpiryDate: String(additionalInformation["linkExpiryDate"]),
            });
        }
    }

    return {
        data: data,
        error: error,
    };
};

// TODO move in core-ui after branding changes are done
interface IFXPrepareForBankTransferRequest {
    quoteId?: string; //The ID of the quote to link to the order. Omit for non-FX (base currency) payments.
}
interface IFXPrepareForBankTransferResponse {
    orderId: string; //The ID of the order created for the payment.
    paymentReference: string;
    sortCode?: string;
    accountNumber?: string;
    accountHolderName: string;
    iban?: string;
    bic?: string;
    returnUrl?: string; //Partner return URL
    quoteExpiryDate?: string;
    quoteRate?: string;
    amountInBaseCurrency: string;
    baseCurrency: string;
    amountInLocalCurrency?: string;
    localCurrency?: string;
}
// end of TODO

/**
 * Internal API call to prepare for bank transfer for IFX.
 * @return {[{IFXPrepareForBankTransferResponse, null}]}
 */
export const prepareForBankTransfer = async () => {
    const ifxStore = useIFXStore();
    const paymentStore = usePaymentsStore();
    const checkoutStore = useCheckoutStore();

    const orderId = checkoutStore.orderId;
    const quoteId = paymentStore.selectedProviderDetails?.quoteId;
    const payload: IFXPrepareForBankTransferRequest = quoteId
        ? keysToSnakeCase({ quoteId })
        : {};

    /** Define the behaviour if an error occurs during the API call */
    const errorHandlerConfig = {
        userAction: "retry", // user needs to retry the action
        sentryTransactionName: "prepareForBankTransfer",
        redirectTo: "payment-status", // TODO check this redirect after the order status page is implemented for IFX payments
    };

    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/orders/${orderId}/prepare-for-bank-transfer`,
            HTTP_METHOD.POST,
            getCommonHeaderParams(),
            payload,
        ),
        errorHandlerConfig,
    );

    if (data !== null) {
        const bankDetails: IFXPrepareForBankTransferResponse =
            keysToCamelCase(data);
        ifxStore.setIFXBankTransferInformation(bankDetails);
        return bankDetails;
    } else {
        return null;
    }
};
